import React from 'react';
import { SiteSection, SiteSectionHeader } from '../../components/site';
import { PRODUCT_ICON_MAP } from '../product-subpage/productIcons';
import { SolutionOfferingsShowcase } from './SolutionOfferingsShowcase';
import { SolutionPillarExplorer } from './SolutionPillarExplorer';
import { PlatformSubFeatures } from '../platform';
import { SolutionReveal } from './SolutionReveal';
import { SolutionHospitalityEcosystem } from './SolutionHospitalityEcosystem';
import { SolutionHospitalityImplementation } from './SolutionHospitalityImplementation';
import { SolutionHospitalityStatsRibbon } from './SolutionHospitalityStatsRibbon';
import { SolutionHospitalityGuestJourney } from './SolutionHospitalityGuestJourney';
import { SolutionHospitalityModules } from './SolutionHospitalityModules';
import { SolutionHospitalityCapabilities } from './SolutionHospitalityCapabilities';
import { SolutionHospitalityPlatform } from './SolutionHospitalityPlatform';
import { SolutionCommercialBentoVerticals } from './SolutionCommercialBentoVerticals';
import { SolutionCommercialCtaBanner } from './SolutionCommercialCtaBanner';
import { SolutionCommercialPlatformModules } from './SolutionCommercialPlatformModules';
import { SolutionCommercialStatsRibbon } from './SolutionCommercialStatsRibbon';
import { SolutionCommercialTransform } from './SolutionCommercialTransform';
import { SolutionTransformSection } from './SolutionTransform';
import { SolutionUseCaseExplorer } from './SolutionUseCaseExplorer';
import { SolutionFloorAutomation } from './SolutionFloorAutomation';
import { SolutionCtaBanner } from './SolutionCtaBanner';
import { SolutionManufacturingLifecycle } from './SolutionManufacturingLifecycle';
import { SolutionManufacturingStack } from './SolutionManufacturingStack';
import { solutionPageExtras } from './solutionPageExtras';
import type { SolutionExperienceJourney, SolutionSubpageConfig } from './types';

const SolutionJourneySection: React.FC<{
  journey: SolutionExperienceJourney;
}> = ({ journey }) => (
  <SiteSection id="experience-journey" tone="muted" className="solution-journey">
    <SiteSectionHeader
      eyebrow={journey.eyebrow}
      title={journey.title}
      subtitle={journey.subtitle}
      align="center"
    />
    <ol className="solution-journey__steps">
      {journey.steps.map((step) => {
        const Icon = PRODUCT_ICON_MAP[step.icon];
        return (
          <li key={step.step} className={`solution-journey__step solution-journey__step--accent-${step.step}`}>
            <span className="solution-journey__icon" aria-hidden>
              <Icon size={22} strokeWidth={1.75} />
            </span>
            <span className="solution-journey__num">{step.step}</span>
            <div className="solution-journey__body">
              <h3>{step.title}</h3>
              <p>{step.description}</p>
            </div>
          </li>
        );
      })}
    </ol>
  </SiteSection>
);

function getCtaTitleForSlug(slug: string) {
  switch (slug) {
    case 'smart-house':
      return 'Deliver a seamless smart living experience for every resident touchpoint';
    case 'smart-hospitality':
      return 'Upgrade guest experiences with connected, automated hospitality operations';
    case 'smart-manufacturing':
      return 'Optimize production with real-time visibility and smarter factory operations';
    case 'commercial-lighting':
      return 'Modernize buildings with intelligent lighting that saves energy and improves comfort';
    default:
      return 'Get started with a premium, connected experience built for your business';
  }
}

export const SolutionPremiumContent: React.FC<{
  config: SolutionSubpageConfig;
}> = ({ config }) => {
  const extras = config.pageExtras ?? solutionPageExtras[config.slug];
  const processTitle = extras.processTitle ?? 'Partnership process';

  if (config.slug === 'commercial-lighting') {
    return (
      <>
        {extras.bentoVerticals ? (
          <SolutionReveal>
            <SolutionCommercialBentoVerticals verticals={extras.bentoVerticals} />
          </SolutionReveal>
        ) : null}

        {extras.platformModules ? (
          <SolutionReveal>
            <SolutionCommercialPlatformModules section={extras.platformModules} />
          </SolutionReveal>
        ) : null}

        <SolutionReveal>
          <SolutionCommercialTransform transform={extras.transform} />
        </SolutionReveal>

        {extras.statsRibbon ? (
          <SolutionReveal>
            <SolutionCommercialStatsRibbon ribbon={extras.statsRibbon} />
          </SolutionReveal>
        ) : null}

        {extras.commercialCta ? (
          <SolutionReveal>
            <div id="get-started">
              <SolutionCommercialCtaBanner cta={extras.commercialCta} />
            </div>
          </SolutionReveal>
        ) : null}
      </>
    );
  }

  return (
    <>
      <SolutionReveal>
        <SolutionFloorAutomation verticals={config.premium.verticals} />
      </SolutionReveal>

      <SolutionReveal>
        {config.slug === 'smart-hospitality' && extras.ecosystem ? (
          <SolutionHospitalityEcosystem ecosystem={extras.ecosystem} />
        ) : (
          <SolutionTransformSection
            transform={extras.transform}
            variant={config.slug === 'smart-manufacturing' ? 'manufacturing' : 'default'}
          />
        )}
      </SolutionReveal>

      {config.slug === 'smart-hospitality' && extras.modules ? (
        <SolutionReveal>
          <SolutionHospitalityModules modules={extras.modules} />
        </SolutionReveal>
      ) : config.offerings.length > 0 ? (
        <SolutionReveal>
          <SolutionOfferingsShowcase offerings={config.offerings} />
        </SolutionReveal>
      ) : null}

      <SolutionReveal>
        {config.slug === 'smart-manufacturing' ? (
          <SolutionManufacturingLifecycle
            journey={extras.journey}
          />
        ) : config.slug === 'smart-hospitality' && extras.guestJourney ? (
          <SolutionHospitalityGuestJourney journey={extras.guestJourney} />
        ) : (
          <SolutionJourneySection journey={extras.journey} />
        )}
      </SolutionReveal>

      {config.slug !== 'smart-manufacturing' && (
      <SolutionReveal>
        {config.slug === 'smart-hospitality' &&
        extras.implementationJourney &&
        extras.statsRibbon ? (
          <>
            <SolutionHospitalityImplementation journey={extras.implementationJourney} />
            <SolutionHospitalityStatsRibbon ribbon={extras.statsRibbon} />
          </>
        ) : (
          <SiteSection
            tone="muted"
            className={`solution-partnership solution-partnership--${config.slug} solution-partnership--colorful`}
          >
            <h3 className="solution-process__title">{processTitle}</h3>
            <ol className="solution-process__steps solution-process__steps--timeline solution-process__steps--colorful">
              {config.process.map((step) => (
                <li
                  key={step.step}
                  className={`solution-process__step solution-process__step--accent-${step.step}`}
                >
                  <span className="solution-process__num">{step.step}</span>
                  <div className="solution-process__body">
                    <h4>{step.title}</h4>
                    <p>{step.description}</p>
                    <ul>
                      {step.bullets.map((b) => (
                        <li key={b}>{b}</li>
                      ))}
                    </ul>
                  </div>
                </li>
              ))}
            </ol>

            <div className={`solution-stats solution-stats--${config.slug} solution-stats--colorful`}>
              {config.stats.map((stat, index) => (
                <div
                  key={stat.label}
                  className={`solution-stats__item solution-stats__item--accent-${index}`}
                >
                  <span className="solution-stats__value">{stat.value}</span>
                  <span className="solution-stats__label">{stat.label}</span>
                </div>
              ))}
            </div>
          </SiteSection>
        )}
      </SolutionReveal>
      )}

      {config.slug !== 'smart-manufacturing' && config.slug !== 'smart-hospitality' && (
        <SolutionReveal>
          <SolutionCtaBanner
            title={getCtaTitleForSlug(config.slug)}
            ctaLabel="Request demo"
            ctaTo="/contact"
            imageSrc="/images/cta/solution-cta-robot.webp"
            imageAlt="Industrial robot arm"
          />
        </SolutionReveal>
      )}

      <SolutionReveal>
        {config.slug === 'smart-manufacturing' ? (
          <SolutionManufacturingStack pillars={config.premium.pillars} stats={config.stats} />
        ) : config.slug === 'smart-hospitality' ? (
          <SolutionHospitalityPlatform pillars={config.premium.pillars} />
        ) : (
          <SolutionPillarExplorer pillars={config.premium.pillars} />
        )}
      </SolutionReveal>

      <SolutionReveal>
        <SolutionUseCaseExplorer useCases={config.premium.useCases} />
      </SolutionReveal>

      <SolutionReveal>
        {config.slug === 'smart-hospitality' ? (
          <SolutionHospitalityCapabilities capabilities={config.capabilities} />
        ) : (
          <PlatformSubFeatures capabilities={config.capabilities} />
        )}
      </SolutionReveal>
    </>
  );
};
